import { BBox, Feature, LineString, MultiLineString, Point, Position } from "geojson";
import geojsonRbush from "geojson-rbush";
import { MultiUndirectedGraph } from "graphology";
import minBy from "lodash/minBy";

import {
  destination as turfDestination,
  distance as turfDistance,
  length as turfLength,
  lineString as turfLineString,
  point as turfPoint,
} from "fond/turf";

export type NodeId = string;
export type EdgeId = string;

export interface PathEdge {
  length: number;
  featureId: string | number;
  coordinates: Position[];
}

type NodeAttributes = { coord: [number, number] };

export type PathGraph = MultiUndirectedGraph<NodeAttributes, PathEdge>;

/**
 * The default merge tolerance, in metres.
 */
const DEFAULT_MERGE_TOLERANCE_METERS = 1.0;

/**
 * A graph node merger for combining geospatially close nodes together.
 */
class NodeMerger {
  private index = geojsonRbush<Point, { nodeId: NodeId }>();

  private coords: Map<NodeId, [number, number]> = new Map();

  private nextId = 0;

  constructor(private toleranceMeters: number) {}

  getOrCreate(coord: Position): NodeId {
    const within = this.index
      .search(this.bboxAround(coord))
      .features.map((f) => ({
        nodeId: f.properties!.nodeId,
        distanceMeters: turfDistance(coord, this.coords.get(f.properties!.nodeId)!, { units: "meters" }),
      }))
      .filter((c) => c.distanceMeters <= this.toleranceMeters);

    // If there are multiple nodes within distance then we merge with the closer one. This can happen
    // in a situation like:
    //
    //    X1 ------ <coord> ----------- X2
    //
    // where coord is within toleranceMeters of both X1 and X2, but X1 and X2 are not within toleranceMeters of each other.
    const best = minBy(within, (c) => c.distanceMeters);
    if (best) return best.nodeId;

    const newId: NodeId = `n${this.nextId}`;
    this.nextId += 1;
    this.coords.set(newId, [coord[0], coord[1]]);
    this.index.insert(turfPoint(coord, { nodeId: newId }));
    return newId;
  }

  coordOf(nodeId: NodeId): [number, number] {
    return this.coords.get(nodeId)!;
  }

  // Search box of "radius" `toleranceMeters` around `coord`.
  private bboxAround(coord: Position): BBox {
    const [, northLat] = turfDestination(coord, this.toleranceMeters, 0, { units: "meters" }).geometry.coordinates;
    const [eastLng] = turfDestination(coord, this.toleranceMeters, 90, { units: "meters" }).geometry.coordinates;
    const dLat = northLat - coord[1];
    const dLng = eastLng - coord[0];
    return [coord[0] - dLng, coord[1] - dLat, coord[0] + dLng, coord[1] + dLat];
  }
}

/**
 * Builds an undirected weighted multigraph based on a collection of (Multi)LineStrings.
 *
 * The nodes of this graph are the endpoints of each LineString, where endpoints that
 * are within `mergeToleranceMeters` metres of each other are merged into a single node.
 *
 * The edges of this graph are the individual LineStrings, with a weight equal to their
 * geometric length.
 *
 * Each feature must have either `feature.id` or `feature.properties.id` defined to be included
 * in the graph. Any feature without these is skipped. It is the caller's responsibility to ensure
 * these IDs are unique across features.
 */
export function buildPathGraph(features: Array<Feature<LineString | MultiLineString>>, options: { mergeToleranceMeters?: number } = {}): PathGraph {
  const merger = new NodeMerger(options.mergeToleranceMeters ?? DEFAULT_MERGE_TOLERANCE_METERS);
  const graph = new MultiUndirectedGraph<NodeAttributes, PathEdge>();

  const addEdge = (coords: Position[], featureId: string | number, suffix: string): void => {
    if (coords.length < 2) return;
    const a = merger.getOrCreate(coords[0]);
    const b = merger.getOrCreate(coords[coords.length - 1]);

    const edgeId: EdgeId = `${featureId}${suffix}`;
    const length = turfLength(turfLineString(coords), { units: "meters" });
    graph.mergeNode(a, { coord: merger.coordOf(a) });
    graph.mergeNode(b, { coord: merger.coordOf(b) });
    graph.addEdgeWithKey(edgeId, a, b, { length, featureId, coordinates: coords });
  };

  features.forEach((feature) => {
    const featureId = (feature.id ?? feature.properties?.id) as string | number | undefined;
    if (!featureId) return;

    if (feature.geometry.type === "LineString") {
      addEdge(feature.geometry.coordinates, featureId, "");
    } else if (feature.geometry.type === "MultiLineString") {
      // Disambiguate multilinestring components using a suffix
      feature.geometry.coordinates.forEach((part, j) => addEdge(part, featureId, `#${j}`));
    }
  });

  return graph;
}

/**
 * Return linestring coordinates for a path of edges through a PathGraph.
 *
 * The returned linestring is guaranteed to be oriented in the direction of the path,
 * regardless of the original orientation of the edges in the graph.
 */
export function buildPathCoordinates(graph: PathGraph, sourceNodeId: NodeId, edgeIds: EdgeId[]): Position[] {
  const out: Position[] = [];
  let cursor = sourceNodeId;
  for (const [i, edgeId] of edgeIds.entries()) {
    if (!graph.hasEdge(edgeId)) return [];

    // The PathGraph stores edge coordinates with an arbitrary orientation, so we may need to reverse
    // those coordinates to ensure the path flows from source to target. For example:
    //
    //   A -----> B <----- C <----- D
    //
    // If the path is [A, B, C, D] then the coordinates of the edge AB are oriented in the correct direction,
    // but the coordinates of BC and CD would be reversed.
    const { coordinates } = graph.getEdgeAttributes(edgeId);
    const ordered = graph.source(edgeId) === cursor ? coordinates : [...coordinates].reverse();
    out.push(...(i === 0 ? ordered : ordered.slice(1)));
    cursor = graph.opposite(cursor, edgeId);
  }

  return out;
}
